import React, { useState } from 'react';
import StepperHeading from '../StepperHeading';
import { Grid, InputLabel, Stack, Select, MenuItem, IconButton, TextField, Box, Typography, Divider } from '@mui/material';
import { Checkbox, DatePicker, theme } from 'antd';
import { FiCalendar } from 'react-icons/fi';
import { MdAccessTime } from 'react-icons/md';
import moment from 'moment';
import dayjs from 'dayjs';
import { AppColors } from '../../../../Utils/helper/AppColors.js';
// import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
	ArrowDownIcon,
	CalendarIcon,
	CallIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ClockIcon,
	InfoIcon,
} from '../../../../Components/Icons/Icons';
import AdvancedOptions from './AdvancedOptions/AdvancedOptions.js';

const { RangePicker } = DatePicker;

export default function Scheduling({ details, setDetails , setIsDialogOpenCheckData }) {
	console.log('Scheduling details:', details);

	const [openOptions, setOpenOptions] = useState(details?.advancedOptionsEnabled || false);
    
	const handleInputChange = (key, value) => {
		setDetails({
			...details,
			scheduling: {
				...details?.scheduling,
				[key]: value,
			},
		});
	};

	// Define a reusable function for handling date selection
	const handleDateSelection = (key, selectedDate) => {
		const formattedDate = moment(new Date(selectedDate)).format('YYYY-MM-DD');

		if (key === 'startDate') {
			const endDate = details?.scheduling?.endDate;

			if (endDate && moment(formattedDate).isAfter(endDate)) {
				// Swap startDate and endDate
				setDetails({
					...details,
					scheduling: {
						...details?.scheduling,
						startDate: endDate,
						endDate: formattedDate,
					},
				});
			} else {
				handleInputChange('startDate', formattedDate);
			}
		} else if (key === 'endDate') {
			const startDate = details?.scheduling?.startDate;

			if (startDate && moment(formattedDate).isBefore(startDate)) {
				// Swap startDate and endDate
				setDetails({
					...details,
					scheduling: {
						...details?.scheduling,
						startDate: formattedDate,
						endDate: startDate,
					},
				});
			} else {
				handleInputChange('endDate', formattedDate);
			}
		}
	};

	const handleDateRange = (e) => {
		let startDate = moment(new Date(e[0])).format(moment.HTML5_FMT.DATE);
		let endDate = moment(new Date(e[1])).format(moment.HTML5_FMT.DATE);
		setDetails({
			...details,
			scheduling: {
				...details?.scheduling,
				startDate,
				endDate,
			},
		});
	};

	// Get today's date
	const today = dayjs();

	// Generate 12 months from today (including next year if you want)
	const monthOptions = [];
	for (let i = 0; i < 12; i++) {
		const date = today.add(i, 'month');
		monthOptions.push({
			value: `${date.year()}-${date.month()}`,
			label: date.format('MMM YYYY'),
			year: date.year(),
			month: date.month(),
		});
	}

	// State for selected month/year for start and end
	const [selectedMonth, setSelectedMonth] = React.useState(
		details?.scheduling?.startDate
			? `${dayjs(details.scheduling.startDate).year()}-${dayjs(details.scheduling.startDate).month()}`
			: `${today.year()}-${today.month()}`,
	);
	const [selectedEndMonth, setSelectedEndMonth] = React.useState(
		details?.scheduling?.endDate
			? `${dayjs(details.scheduling.endDate).year()}-${dayjs(details.scheduling.endDate).month()}`
			: `${today.year()}-${today.month()}`,
	);

	// Add these states:
	const [startDayOffset, setStartDayOffset] = React.useState(0);
	const [endDayOffset, setEndDayOffset] = React.useState(0);

	// Helper to get year/month from value
	const getYearMonth = (val) => {
		const [year, month] = val.split('-').map(Number);
		return { year, month };
	};

	// Days in selected month for start and end
	const { year: startYear, month: startMonth } = getYearMonth(selectedMonth);
	const daysInStartMonth = dayjs(`${startYear}-${startMonth + 1}-01`).daysInMonth();

	const { year: endYear, month: endMonth } = getYearMonth(selectedEndMonth);
	const daysInEndMonth = dayjs(`${endYear}-${endMonth + 1}-01`).daysInMonth();

	// Helper to get the first selectable day for start month
	const getStartFirstDay = () => {
		const { year, month } = getYearMonth(selectedMonth);
		return year === today.year() && month === today.month() ? today.date() : 1;
	};
	const startFirstDay = getStartFirstDay();
	const startLastDay = daysInStartMonth;

	// For the 15-day window
	const startWindowStart = startFirstDay + startDayOffset;
	const startWindowEnd = Math.min(startWindowStart + 14, startLastDay);

	// For the 15-day window for end date
	const endFirstDay = (() => {
		const { year, month } = getYearMonth(selectedEndMonth);
		const startDate = details?.scheduling?.startDate ? dayjs(details.scheduling.startDate) : today;
		return year === startDate.year() && month === startDate.month() ? startDate.date() : 1;
	})();
	const endLastDay = daysInEndMonth;
	const endWindowStart = endFirstDay + endDayOffset;
	const endWindowEnd = Math.min(endWindowStart + 14, endLastDay);

	// Handlers for month change
	const handleMonthChange = (event) => {
		const val = event.target.value;
		setSelectedMonth(val);
		const { year, month } = getYearMonth(val);
		let newDay = year === today.year() && month === today.month() ? today.date() : 1;
		const newDate = dayjs(`${year}-${month + 1}-${newDay}`);
		handleInputChange('startDate', newDate.format('YYYY-MM-DD'));
	};

	const handleEndMonthChange = (event) => {
		const val = event.target.value;
		setSelectedEndMonth(val);
		const { year, month } = getYearMonth(val);
		const startDate = details?.scheduling?.startDate ? dayjs(details.scheduling.startDate) : today;
		let minDay = year === startDate.year() && month === startDate.month() ? startDate.date() : 1;
		const newDate = dayjs(`${year}-${month + 1}-${minDay}`);
		handleInputChange('endDate', newDate.format('YYYY-MM-DD'));
	};

	// Handlers for day click
	const handleDayClick = (day) => {
		const { year, month } = getYearMonth(selectedMonth);
		const newDate = dayjs(`${year}-${month + 1}-${day}`);
		handleInputChange('startDate', newDate.format('YYYY-MM-DD'));
	};
	const handleEndDayClick = (day) => {
		const { year, month } = getYearMonth(selectedEndMonth);
		const newDate = dayjs(`${year}-${month + 1}-${day}`);
		handleInputChange('endDate', newDate.format('YYYY-MM-DD'));
	};

	// Handler for arrows
	const handleStartPrev = () => {
		if (startWindowStart > startFirstDay) {
			setStartDayOffset(Math.max(0, startDayOffset - 15));
		} else if (startMonth > today.month() || startYear > today.year()) {
			// Move to previous month if possible
			const prevMonth = dayjs(`${startYear}-${startMonth + 1}-01`).subtract(1, 'month');
			const prevMonthVal = `${prevMonth.year()}-${prevMonth.month()}`;
			setSelectedMonth(prevMonthVal);
			setStartDayOffset(Math.max(0, dayjs(`${prevMonth.year()}-${prevMonth.month() + 1}-01`).daysInMonth() - 15));
		}
	};
	const handleStartNext = () => {
		if (startWindowEnd < startLastDay) {
			setStartDayOffset(startDayOffset + 15);
		} else if (startMonth < today.add(11, 'month').month() || startYear < today.add(11, 'month').year()) {
			// Move to next month if possible
			const nextMonth = dayjs(`${startYear}-${startMonth + 1}-01`).add(1, 'month');
			const nextMonthVal = `${nextMonth.year()}-${nextMonth.month()}`;
			setSelectedMonth(nextMonthVal);
			setStartDayOffset(0);
		}
	};

	// Arrow handlers for end date
	const handleEndPrev = () => {
		if (endWindowStart > endFirstDay) {
			setEndDayOffset(Math.max(0, endDayOffset - 15));
		} else {
			const { year, month } = getYearMonth(selectedEndMonth);
			const startDate = details?.scheduling?.startDate ? dayjs(details.scheduling.startDate) : today;
			const canGoPrev = year > startDate.year() || (year === startDate.year() && month > startDate.month());
			if (canGoPrev) {
				const prevMonth = dayjs(`${year}-${month + 1}-01`).subtract(1, 'month');
				const prevMonthVal = `${prevMonth.year()}-${prevMonth.month()}`;
				setSelectedEndMonth(prevMonthVal);
				const prevMonthDays = dayjs(`${prevMonth.year()}-${prevMonth.month() + 1}-01`).daysInMonth();
				setEndDayOffset(Math.max(0, prevMonthDays - 15));
			}
		}
	};
	const handleEndNext = () => {
		if (endWindowEnd < endLastDay) {
			setEndDayOffset(endDayOffset + 15);
		} else {
			const { year, month } = getYearMonth(selectedEndMonth);
			const maxMonth = today.add(11, 'month');
			const canGoNext = year < maxMonth.year() || (year === maxMonth.year() && month < maxMonth.month());
			if (canGoNext) {
				const nextMonth = dayjs(`${year}-${month + 1}-01`).add(1, 'month');
				const nextMonthVal = `${nextMonth.year()}-${nextMonth.month()}`;
				setSelectedEndMonth(nextMonthVal);
				setEndDayOffset(0);
			}
		}
	};

	// Reset offset when month changes
	React.useEffect(() => {
		setStartDayOffset(0);
	}, [selectedMonth]);

	// When start month changes, sync end month and reset offset
	React.useEffect(() => {
		setSelectedEndMonth(selectedMonth);
		setEndDayOffset(0);
	}, [selectedMonth]);

	// When start date changes, if end date is before start date, sync end date
	React.useEffect(() => {
		const startDate = details?.scheduling?.startDate;
		const endDate = details?.scheduling?.endDate;
		if (startDate && endDate && dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
			handleInputChange('endDate', startDate);
		}
	}, [details?.scheduling?.startDate]);

	// Reset offset when end month changes
	React.useEffect(() => {
		setEndDayOffset(0);
	}, [selectedEndMonth]);

	// useEffect(() => {
	// 	console.log('Scheduling details updated:', details);
	// }, []);

	return (
		<Box p={3}>
			{/* <StepperHeading title="Scheduling" subTitle="Fill in the schedule of the campaign" /> */}
			<Grid container spacing={2} >
				<Grid item xs={12}>
					{/* <RangePicker
						minDate={dayjs()}
						defaultValue={[
							details?.scheduling?.startDate ? dayjs(details?.scheduling?.startDate, 'YYYY-MM-DD') : null,
							details?.scheduling?.endDate ? dayjs(details?.scheduling?.endDate, 'YYYY-MM-DD') : null,
						]}
						format="YYYY-MM-DD"
						onChange={handleDateRange}
						getPopupContainer={(node) => node.parentNode}
						style={{ width: '100%' }}
						suffixIcon={<FiCalendar color={AppColors.LIGHT1} size={18} />}
					/> */}
					<Stack direction="column" sx={{ width: '100%' }}>
						<Stack direction="column" spacing={1}>
							<InputLabel
								sx={{
									display: 'flex',
									alignItems: 'center',
									fontSize: 16,
									fontWeight: 600,
									fontFamily: 'Poppins',
									color: AppColors.HEADING_TITLE4,
									gap: '5px',
								}}
							>
								<CalendarIcon size={15} /> Campaign Start Date
							</InputLabel>
							<Stack direction="row" justifyContent={'space-between'} alignItems="center">
								<Select
									// IconComponent={ArrowDownIcon}
									value={selectedMonth}
									onChange={handleMonthChange}
									size="small"
									sx={{
										position: 'relative',
										minWidth: 160,
										mb: '4px',
										height: 40,
										fontFamily: 'Poppins',
										fontWeight: 400,
										fontSize: 14,
										lineHeight: '150%',
										letterSpacing: 0,
										verticalAlign: 'middle',
										'& .MuiSelect-select': {
											height: 40,
											display: 'flex',
											alignItems: 'center',
											fontFamily: 'Poppins',
											fontWeight: 400,
											fontSize: 14,
											lineHeight: '150%',
											letterSpacing: 0,
											verticalAlign: 'middle',
										},
										// '& .MuiSelect-icon': {

										// 	right: '30px', // 👈 move it further to the left
										// 	top: 'calc(50% - 12px)', // 👈 vertically center if needed
										// },
									}}
								>
									{monthOptions.map((month) => (
										<MenuItem key={month.value} value={month.value}>
											{month.label}
										</MenuItem>
									))}
								</Select>
								<Stack direction="row" spacing={1}>
									<IconButton
										sx={(theme) => ({
											border: `1px solid ${theme.palette.secondary.orange || theme.palette.primary.lighter_shade}`,
											borderRadius: '5px',
											height: '40px',
											width: '40px',
										})}
										onClick={handleStartPrev}
										size="small"
									>
										<ChevronLeftIcon size={10} color={AppColors?.orange || theme.palette.primary.lighter_shade} />
									</IconButton>
									<IconButton
										sx={(theme) => ({
											border: `1px solid ${theme.palette.secondary.orange || theme.palette.primary.lighter_shade}`,
											borderRadius: '5px',
											height: '40px',
											width: '40px',
										})}
										onClick={handleStartNext}
										size="small"
									>
										<ChevronRightIcon size={10} color={AppColors?.orange || theme.palette.primary.lighter_shade} />
									</IconButton>
								</Stack>
							</Stack>
							{/* Scrollable date slider for start date, only 15 days */}
							<Stack direction="row" spacing={2} sx={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
								{Array.from({ length: startLastDay - startFirstDay + 1 }, (_, i) => startFirstDay + i)
									.slice(startDayOffset, startDayOffset + 15)
									.map((day) => {
										const dateObj = dayjs(`${startYear}-${startMonth + 1}-${day}`);
										return (
											<Box
												key={day}
												sx={(theme) => ({
													padding: '9px 16px',
													display: 'flex',
													flexDirection: 'column',
													justifyContent: 'center',
													alignItems: 'center',
													border: '1px solid',
													borderColor: theme.palette.primary.lightest_shade,
													borderRadius: '4px',
													cursor: 'pointer',
													backgroundColor:
														details?.scheduling?.startDate && dateObj.isSame(dayjs(details?.scheduling?.startDate), 'day')
															? theme.palette.primary.lightest_shade
															: '#fff',
													width: '69px',
													hight: '82px',
													boxShadow:
														details?.scheduling?.endDate && dateObj.isSame(dayjs(details?.scheduling?.endDate), 'day')
															? '0px 4px 8px 0px #7F56D933'
															: 'none',
												})}
												onClick={() => handleDayClick(day)}
											>
												<Typography fontSize={14} sx={(theme) => ({ color: theme.palette.primary.darker_primary })}>
													{dateObj.format('ddd')}
												</Typography>
												<Typography fontSize={22} sx={(theme) => ({ color: theme.palette.primary.darker_primary })}>
													{day}
												</Typography>
											</Box>
										);
									})}
							</Stack>
						</Stack>
						
						<Divider sx={{ my: '24px'  , borderBottomWidth: '2px' }} />

						<Stack direction="column" spacing={1} >
							<InputLabel
								sx={{
									display: 'flex',
									alignItems: 'center',
									fontSize: 16,
									fontWeight: 600,
									fontFamily: 'Poppins',
									color: AppColors.HEADING_TITLE4,
									gap: '5px',
								}}
							>
								<CalendarIcon size={15} /> Campaign End Date
							</InputLabel>
							<Stack direction="row" justifyContent={'space-between'} alignItems="center">
								<Select
									value={selectedEndMonth}
									onChange={handleEndMonthChange}
									size="small"
									sx={{
										position: 'relative',
										minWidth: 160,
										mb: '4px',
										height: 40,
										fontFamily: 'Poppins',
										fontWeight: 400,
										fontSize: 14,
										lineHeight: '150%',
										letterSpacing: 0,
										verticalAlign: 'middle',
										'& .MuiSelect-select': {
											height: 40,
											display: 'flex',
											alignItems: 'center',
											fontFamily: 'Poppins',
											fontWeight: 400,
											fontSize: 14,
											lineHeight: '150%',
											letterSpacing: 0,
											verticalAlign: 'middle',
										},
									}}
								>
									{monthOptions.map((month) => {
										const { year: startYear, month: startMonth } = getYearMonth(selectedMonth);
										const isDisabled = month.year < startYear || (month.year === startYear && month.month < startMonth);
										return (
											<MenuItem key={month.value} value={month.value} disabled={isDisabled}>
												{month.label}
											</MenuItem>
										);
									})}
								</Select>
								<Stack direction="row" spacing={1}>
									<IconButton
										sx={(theme) => ({
											border: `1px solid ${theme.palette.secondary.orange  || theme.palette.primary.lighter_shade}`,
											borderRadius: '5px',
											height: '40px',
											width: '40px',
										})}
										onClick={handleEndPrev}
										size="small"
									>
										<ChevronLeftIcon size={10} color={AppColors?.orange || theme.palette.primary.lighter_shade} />
									</IconButton>
									<IconButton
										sx={(theme) => ({
											border: `1px solid ${AppColors?.orange || theme.palette.primary.lighter_shade}`,
											borderRadius: '5px',
											height: '40px',
											width: '40px',
										})}
										onClick={handleEndNext}
										size="small"
									>
										<ChevronRightIcon size={10} color={AppColors?.orange || theme.palette.primary.lighter_shade} />
									</IconButton>
								</Stack>
							</Stack>
							{/* Scrollable date slider for end date, only 15 days */}
							<Stack direction="row" spacing={2} sx={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
								{Array.from({ length: endLastDay - endFirstDay + 1 }, (_, i) => endFirstDay + i)
									.slice(endDayOffset, endDayOffset + 15)
									.map((day) => {
										const dateObj = dayjs(`${endYear}-${endMonth + 1}-${day}`);
										return (
											<Box
												key={day}
												sx={(theme) => ({
													padding: '9px 16px',
													display: 'flex',
													flexDirection: 'column',
													justifyContent: 'center',
													alignItems: 'center',
													border: '1px solid',
													borderColor: theme.palette.primary.lightest_shade,
													borderRadius: '4px',
													cursor: 'pointer',
													backgroundColor:
														details?.scheduling?.endDate && dateObj.isSame(dayjs(details?.scheduling?.endDate), 'day')
															? theme.palette.primary.lightest_shade
															: '#fff',
													width: '69px',
													height: '82px',
													boxShadow:
														details?.scheduling?.endDate && dateObj.isSame(dayjs(details?.scheduling?.endDate), 'day')
															? '0px 4px 8px 0px #7F56D933'
															: 'none',
												})}
												onClick={() => handleEndDayClick(day)}
											>
												<Typography fontSize={14} sx={(theme) => ({ color: theme.palette.primary.darker_primary })}>
													{dateObj.format('ddd')}
												</Typography>
												<Typography fontSize={22} sx={(theme) => ({ color: theme.palette.primary.darker_primary })}>
													{day}
												</Typography>
											</Box>
										);
									})}
							</Stack>
						</Stack>
					</Stack>
				</Grid>
				{/* <Grid item xs={4}>
					<InputLabel>Maximum number of tries in a day <span style={{ color: "red" }}>*</span></InputLabel>
				</Grid> */}
				{/* <Grid item xs={8}>
					<TextField
						value={details?.scheduling?.maxTryPerDay}
						onChange={(e) => handleInputChange('maxTryPerDay', e.target.value)}
						sx={{ width: 90 }}
						type="number"
						size="small"
					/>
				</Grid> */}

				{/* <Grid item xs={4}>
					<InputLabel>Auto call before ending of campaign</InputLabel>
				</Grid>
				<Grid item xs={8}>
					<Checkbox
						checked={details?.defaultSchedule}
						onChange={(e) => setDetails({ ...details, defaultSchedule: e.target.checked })}
					/>
				</Grid> */}
			</Grid>

			<Divider sx={{ my: '24px' , borderBottomWidth: '2px' }} />

			<Box display="flex" gap={4} mb={2}>
					{/* Campaign Aligning Timings */}
					<Box flex={1}>
						<InputLabel
							sx={{
								fontSize: 16,
								fontWeight: 600,
								fontFamily: 'Poppins',
								color: AppColors.HEADING_TITLE4,
								mb: 1,
								gap: '5px',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<ClockIcon size={15} /> Campaign Aligning Timings
						</InputLabel>
						<Stack direction="row" gap={2}>
							{/* Start Time */}
							<Box
								sx={(theme) => ({
									width: 323,
									height: 50,
									background: theme.palette.secondary.orange || theme.palette.primary.lightest_shade,
									borderRadius: '5px',
									display: 'flex',
									alignItems: 'center',
									px: 2,
									justifyContent: 'space-between',
								})}
							>
								<Box
									sx={{
										width: 100,
										height: 30,
										display: 'flex',
										alignItems: 'center',
										fontFamily: 'Poppins',
										fontWeight: 400,
										fontSize: 14,
										borderRadius: '1px',
										lineHeight: '150%',
										color:  AppColors.white || '#000000',
										background: 'transparent',
									}}
								>
									Start Time
								</Box>
								<DatePicker
									needConfirm={false}
									minuteStep={15}
									showSecond={false}
									defaultValue={details?.scheduling?.startTime ? dayjs(details?.scheduling?.startTime, 'HH:mm') : null}
									onChange={(e) => handleInputChange('startTime', moment(new Date(e)).format('HH:mm'))}
									getPopupContainer={(node) => node.parentNode}
									picker="time"
									placeholder=""
									use12Hours
									className="custom-date-time-picker"
									suffixIcon={<></>}
									style={{
										background: '#fff',
										borderRadius: '1px',
										width: 140,
										height: 36,
										border: 'none',
									}}
									inputReadOnly
								/>
							</Box>
							{/* End Time */}
							<Box
								sx={(theme) => ({
									width: 323,
									height: 50,
									background: theme.palette.secondary.orange || theme.palette.primary.lightest_shade,
									borderRadius: '5px',
									display: 'flex',
									alignItems: 'center',
									px: 2,
									justifyContent: 'space-between',
								})}
							>
								<Box
									sx={{
										width: 100,
										height: 30,
										display: 'flex',
										alignItems: 'center',
										fontFamily: 'Poppins',
										fontWeight: 400,
										fontSize: 14,
										lineHeight: '150%',
										borderRadius: '100px',
										color: AppColors.white || '#000000',
										background: 'transparent',
									}}
								>
									End Time
								</Box>
								<DatePicker
									needConfirm={false}
									minuteStep={15}
									showSecond={false}
									defaultValue={details?.scheduling?.endTime ? dayjs(details?.scheduling?.endTime, 'HH:mm') : null}
									onChange={(e) => handleInputChange('endTime', moment(new Date(e)).format('HH:mm'))}
									suffixIcon={<></>}
									getPopupContainer={(node) => node.parentNode}
									picker="time"
									placeholder=""
									use12Hours
									borderColor={'red'}
									style={{
										background: '#fff',
										borderRadius: '1px',
										width: 140,
										height: 36,
										border: 'none',
									}}
								/>
							</Box>
						</Stack>
					</Box>

					{/* Maximum number of tries */}
					<Box flex={1} ml={4}>
						<InputLabel
							sx={{
								fontSize: 16,
								fontWeight: 600,
								fontFamily: 'Poppins',
								color: AppColors.orange || AppColors.HEADING_TITLE4,
								mb: 1,
								gap: '5px',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<CallIcon size={15} /> Maximum number of tries
						</InputLabel>
						<TextField
							value={details?.scheduling?.maxTry}
							id="outlined-required"
							onChange={(e) => {
								const value = parseInt(e.target.value);
								if (!isNaN(value) && value > 0) {
									handleInputChange('maxTry', value);
								} else if (e.target.value === '') {
									handleInputChange('maxTry', '');
								}
							}}
							type="string"
							size="small"
							inputProps={{ min: 1 }}
							sx={(theme) => ({
								width: 323,
								height: 50,
								'& .MuiOutlinedInput-root': {
									backgroundColor: theme.palette.secondary.orange || theme.palette.primary.lightest_shade,
									borderRadius: '5px',
									'& fieldset': {
										border: 'none',
									},
									'& .MuiOutlinedInput-input': {
										backgroundColor: 'transparent',
										color: AppColors.white || AppColors.BLACK || '#000',
										height: '30px',
									},
								},
							})}
						/>
					</Box>
				</Box>

				<Divider sx={{ my: '24px' , borderBottomWidth: '3px' }} />

				<AdvancedOptions openOptions={openOptions} setOpenOptions={setOpenOptions} details={details} setDetails={setDetails} setIsDialogOpenCheckData={setIsDialogOpenCheckData} />
		</Box>
	);
}
